import { useEffect, useMemo, useRef, useState } from "react"
import { Button, Card, DatePicker, Space } from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { useCrud } from "@/hooks/useCrud"
import type { UseCrudReturn } from "@/hooks/useCrud"
import { ID } from "@/types/common"
import leadApi from "./api"
import { Lead, LeadDictOption, LeadFormData } from "./types"
import LeadTable from "./components/LeadTable"
import LeadModal from "./components/LeadModal"
import type { Dayjs } from "dayjs"

const LeadManagement = () => {
  const [sourceOptions, setSourceOptions] = useState<LeadDictOption[]>([])
  const [retentionTypeOptions, setRetentionTypeOptions] = useState<LeadDictOption[]>([])
  const [dictOptionsLoading, setDictOptionsLoading] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null)
  const pageSizeRef = useRef(10)
  const loadItemsRef = useRef<UseCrudReturn<Lead, LeadFormData>["loadItems"] | null>(null)

  const selectedMonthText = selectedMonth?.format("YYYY-MM")

  const api = useMemo(
    () => ({
      getList: (params?: { page?: number; pageSize?: number }) =>
        leadApi.getList({
          ...params,
          month: selectedMonthText,
        }),
      create: leadApi.create,
      update: (id: ID, data: LeadFormData | Partial<LeadFormData>) =>
        leadApi.update(id, data as LeadFormData),
      delete: leadApi.delete,
    }),
    [selectedMonthText]
  )

  const {
    items: leads,
    loading,
    opLoading,
    isModalOpen,
    editingItem: editingLead,
    selectedIds,
    setSelectedIds,
    handleAdd,
    handleEdit,
    handleSave,
    handleDelete,
    handleBatchDelete,
    handleCloseModal,
    pagination,
    handlePageChange,
    loadItems,
  } = useCrud<Lead, LeadFormData>({
    api,
    entityName: "客资",
    pagination: {
      enabled: true,
      pageSize: 10,
    },
    autoLoad: false,
  })

  useEffect(() => {
    loadItemsRef.current = loadItems
  }, [loadItems])

  useEffect(() => {
    pageSizeRef.current = pagination.pageSize
  }, [pagination.pageSize])

  useEffect(() => {
    loadItemsRef.current?.({ page: 1, pageSize: pageSizeRef.current })
  }, [selectedMonthText])

  useEffect(() => {
    const loadDictOptions = async () => {
      setDictOptionsLoading(true)
      try {
        const options = await leadApi.getDictOptions()
        setSourceOptions(options.sourceOptions)
        setRetentionTypeOptions(options.retentionTypeOptions)
      } catch (error) {
        console.error("加载客资字典选项失败:", error)
      } finally {
        setDictOptionsLoading(false)
      }
    }

    loadDictOptions()
  }, [])

  const handleMonthChange = (value: Dayjs | null) => {
    setSelectedMonth(value)
    setSelectedIds([])
  }

  return (
    <div className="p-6">
      <Card>
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-gray-500">共 {pagination.total} 条客资</span>
            <DatePicker
              picker="month"
              allowClear
              value={selectedMonth}
              onChange={handleMonthChange}
              placeholder="全部月份"
              style={{ width: 160 }}
            />
          </div>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              loading={opLoading}
            >
              新增客资
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={selectedIds.length === 0}
              loading={opLoading}
            >
              批量删除 {selectedIds.length > 0 && `(${selectedIds.length})`}
            </Button>
          </Space>
        </div>

        <LeadTable
          leads={leads}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          sourceOptions={sourceOptions}
          retentionTypeOptions={retentionTypeOptions}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </Card>

      <LeadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        lead={editingLead}
        loading={opLoading}
        sourceOptions={sourceOptions}
        retentionTypeOptions={retentionTypeOptions}
        optionsLoading={dictOptionsLoading}
      />
    </div>
  )
}

export default LeadManagement
